import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SpeechButton from '../../common/SpeechButton';

interface OrderingItem {
  id: string;
  text: string;
  correctPosition: number;
}

interface OrderingStudentProps {
  config: {
    items: OrderingItem[];
    questionText: string;
    hint?: string;
    voiceCode?: string;
    enableTTS?: boolean;
    orientation?: 'vertical' | 'horizontal';
  };
  onAnswerChange: (index: number, answer: string) => void;
  disabled: boolean;
  feedback: { isCorrect: boolean; correctPosition: number }[] | null;
  initialAnswers: string[];
  assignmentStatus: 'New' | 'Active' | 'InReview';
  hasSubmitted: boolean;
}

const OrderingStudent: React.FC<OrderingStudentProps> = ({
  config,
  onAnswerChange,
  disabled,
  feedback,
  initialAnswers,
  hasSubmitted,
}) => {
  const { items, questionText, hint, voiceCode, enableTTS, orientation = 'vertical' } = config;

  // Initialize ordered items from initialAnswers or shuffle items
  const [orderedItems, setOrderedItems] = useState<OrderingItem[]>(() => {
    if (initialAnswers && initialAnswers.length === items.length) {
      // Reconstruct order from initialAnswers
      return initialAnswers
        .map((itemId) => items.find((item) => item.id === itemId))
        .filter((item): item is OrderingItem => item !== undefined);
    } else {
      // Shuffle items for initial display
      const shuffled = [...items];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    }
  });

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const hasInitialized = useRef(false);

  // Send initial answers once on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      orderedItems.forEach((item, index) => {
        onAnswerChange(index, item.id);
      });
      hasInitialized.current = true;
    }
  }, []);

  // Update parent component when order changes (but not on initial mount)
  useEffect(() => {
    if (hasInitialized.current) {
      orderedItems.forEach((item, index) => {
        onAnswerChange(index, item.id);
      });
    }
  }, [orderedItems]);

  // Handle reset: when initialAnswers becomes empty array, reshuffle
  useEffect(() => {
    if (hasInitialized.current && initialAnswers && initialAnswers.length === 0) {
      // Reset was triggered - reshuffle items
      const shuffled = [...items];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      setOrderedItems(shuffled);
    }
  }, [initialAnswers, items]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (disabled) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || disabled) return;

    const newItems = [...orderedItems];
    const [draggedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem);

    setOrderedItems(newItems);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const isItemCorrect = (item: OrderingItem, currentIndex: number): boolean | null => {
    if (!hasSubmitted || !feedback) return null;
    return item.correctPosition === currentIndex;
  };

  const getItemColor = (item: OrderingItem, currentIndex: number) => {
    if (!hasSubmitted) return '#fafafa';
    const isCorrect = isItemCorrect(item, currentIndex);
    if (isCorrect === true) return '#e8f5e9'; // light green
    if (isCorrect === false) return '#ffebee'; // light red
    return '#fafafa';
  };

  return (
    <Box>
      {/* Question Text */}
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {questionText}
        </Typography>
        {enableTTS && voiceCode && (
          <SpeechButton id={`ordering-question`} text={questionText} voiceCode={voiceCode} />
        )}
      </Box>

      {/* Hint */}
      {hint && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: '#fff3e0' }}>
          <Typography variant="body2" color="textSecondary">
            💡 <strong>Vihje:</strong> {hint}
          </Typography>
        </Paper>
      )}

      {/* Instructions */}
      {!disabled && (
        <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
          Vedä ja pudota kohdat oikeaan järjestykseen
        </Typography>
      )}

      {/* Drag and Drop List */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: orientation === 'horizontal' ? 'row' : 'column',
          gap: orientation === 'horizontal' ? 2 : 0,
          width: '100%',
          overflowX: orientation === 'horizontal' ? 'auto' : 'visible',
          pb: orientation === 'horizontal' ? 2 : 0,
        }}
      >
        {orderedItems.map((item, index) => {
          const isCorrect = isItemCorrect(item, index);
          const isDragging = draggedIndex === index;
          
          return (
            <Paper
              key={item.id}
              draggable={!disabled}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              elevation={isDragging ? 4 : 1}
              sx={{
                backgroundColor: getItemColor(item, index),
                border: isDragging ? '2px solid #1976d2' : '1px solid #e0e0e0',
                borderRadius: 1,
                cursor: disabled ? 'default' : 'grab',
                opacity: isDragging ? 0.5 : 1,
                '&:active': {
                  cursor: disabled ? 'default' : 'grabbing',
                },
                p: 2,
                display: 'flex',
                alignItems: 'center',
                minWidth: orientation === 'horizontal' ? 200 : 'auto',
                mb: orientation === 'vertical' ? 1 : 0,
                flexShrink: 0,
              }}
            >
              {!disabled && (
                <DragIndicatorIcon sx={{ mr: 2, color: 'text.secondary' }} />
              )}
              <Box
                sx={{
                  mr: 2,
                  fontWeight: 'bold',
                  minWidth: 30,
                  textAlign: 'center',
                }}
              >
                {index + 1}.
              </Box>
              <Typography sx={{ flexGrow: 1 }}>{item.text}</Typography>
              {hasSubmitted && isCorrect !== null && (
                <Box ml={2}>
                  {isCorrect ? (
                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                  ) : (
                    <CancelIcon sx={{ color: 'error.main' }} />
                  )}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>

      {/* Show correct order after submission */}
      {hasSubmitted && feedback && (
        <Paper elevation={2} sx={{ p: 2, mt: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Oikea järjestys:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: orientation === 'horizontal' ? 'row' : 'column',
              gap: orientation === 'horizontal' ? 2 : 1,
              mt: 2,
              overflowX: orientation === 'horizontal' ? 'auto' : 'visible',
            }}
          >
            {[...items]
              .sort((a, b) => a.correctPosition - b.correctPosition)
              .map((item, index) => (
                <Paper
                  key={item.id}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: orientation === 'horizontal' ? 200 : 'auto',
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ mr: 2, fontWeight: 'bold' }}>{index + 1}.</Box>
                  <Typography>{item.text}</Typography>
                </Paper>
              ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default OrderingStudent;
